import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { transparenciaService } from '../services/transparenciaService';
import { PortalAcessibilidadeShell, usePortalAcessibilidade } from '../components/PortalAcessibilidade';

const TransparenciaParlamentaresPage: React.FC = () => {
  const { tema, barra } = usePortalAcessibilidade();
  const [busca, setBusca] = useState('');
  const { data = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['transparencia-parlamentares'],
    queryFn: transparenciaService.parlamentares,
    staleTime: 5 * 60 * 1000,
  });

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR');
    return termo ? data.filter(p => p.nome.toLocaleLowerCase('pt-BR').includes(termo)) : data;
  }, [data, busca]);

  return (
    <PortalAcessibilidadeShell tema={tema} barra={barra}>
      <header className={tema.header}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${tema.headerKicker}`}>Portal da Transparência</p>
            <h1 className="mt-1 text-2xl font-bold">Emendas por parlamentar</h1>
            <p className={`mt-1 text-sm ${tema.headerText}`}>Escolha um parlamentar para ver as emendas por exercício e a situação de cada uma.</p>
          </div>
          <Link to="/transparencia" className={`rounded-md border px-4 py-2 text-sm font-semibold ${tema.headerButton}`}>
            Todas as emendas
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <label className="block text-sm font-medium">
          Buscar parlamentar
          <span className="relative mt-1 block max-w-md">
            <MagnifyingGlassIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${tema.muted}`} />
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Nome do parlamentar"
              className={`pl-9 ${tema.input}`}
            />
          </span>
        </label>

        {isLoading && <p className="py-16 text-center text-sm">Carregando parlamentares...</p>}
        {isError && (
          <div className="py-12 text-center">
            <p className="text-sm text-red-500">Não foi possível carregar os parlamentares.</p>
            <button type="button" onClick={() => refetch()} className={`mt-3 rounded-md border px-3 py-2 text-sm ${tema.secondary}`}>Tentar novamente</button>
          </div>
        )}
        {!isLoading && !isError && filtrados.length === 0 && (
          <p className="py-12 text-center font-semibold">Nenhum parlamentar encontrado.</p>
        )}

        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtrados.map(p => (
            <li key={p.slug}>
              <Link
                to={`/transparencia/parlamentares/${encodeURIComponent(p.slug)}`}
                className={`block h-full rounded-xl p-5 hover:shadow ${tema.card}`}
              >
                <p className="font-semibold">{p.nome}</p>
                <p className={`mt-1 text-sm ${tema.muted}`}>
                  {[p.bancada ? 'Bancada' : null, p.partido, p.esfera].filter(Boolean).join(' · ') || 'Parlamentar'}
                </p>
                <p className="mt-3 text-sm">
                  <span className="font-bold">{p.totalEmendas.toLocaleString('pt-BR')}</span>{' '}
                  {p.totalEmendas === 1 ? 'emenda publicada' : 'emendas publicadas'}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </PortalAcessibilidadeShell>
  );
};

export default TransparenciaParlamentaresPage;
